import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, ShieldCheck, Clock } from "lucide-react";

/**
 * Tier 3 — hand-typed bank account proven by Stripe micro-deposits.
 * Admin-First mandate: `scope="funding"` (business ACH source) and
 * `scope="employee_admin"` (a specific employee, admin-only) are the ONLY
 * entry paths. There is deliberately no employee-facing scope.
 */
export const ManualBankEntry = ({ scope = "funding", employeeId, state, onChanged }) => {
  const base = scope === "funding" ? "/funding/manual-bank" : `/employees/${employeeId}/bank/manual`;
  if (scope !== "funding" && !employeeId) {
    throw new Error("ManualBankEntry: employeeId is required outside the funding scope");
  }
  const [form, setForm] = useState({ routing_number: "", account_number: "", account_holder_name: "", account_type: "checking" });
  const [amounts, setAmounts] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const pending = state?.status === "pending_verification" || state?.verification === "pending_verification";
  const verified = state?.status === "verified" || state?.verification === "verified";

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/start`, form);
      toast.success(`Bank ••••${data.last4} submitted`, { description: data.message });
      setForm({ routing_number: "", account_number: "", account_holder_name: "", account_type: "checking" });
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cents = [Math.round(parseFloat(amounts.a) * 100), Math.round(parseFloat(amounts.b) * 100)];
      const { data } = await api.post(`${base}/verify`, { amounts: cents });
      toast.success("Bank account verified", { description: data.message });
      setAmounts({ a: "", b: "" });
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  if (verified) {
    return (
      <div className="hard-card p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`manual-bank-verified-${scope}`}>
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-good" />
          <div>
            <div className="font-heading font-black">Bank verified · standard ACH</div>
            <div className="font-mono text-[11px] text-muted-foreground">
              Account ending {state?.last4 || "••••"} · funds clear in about 48 hours
            </div>
          </div>
        </div>
        <span className="border-2 border-good bg-good/10 text-good px-2 py-1 font-mono text-[9px] uppercase tracking-widest">Verified</span>
      </div>
    );
  }

  return (
    <div className="hard-card p-5" data-testid={`manual-bank-entry-${scope}`}>
      <div className="overline mb-2 flex items-center gap-2"><Building2 className="w-4 h-4" /> § MANUAL ENTRY · MICRO-DEPOSITS</div>
      {pending ? (
        <>
          <div className="border-2 border-warn bg-warn/10 p-3 mb-4 flex items-start gap-2" data-testid={`manual-bank-pending-${scope}`}>
            <Clock className="w-4 h-4 shrink-0 mt-0.5 text-warn" />
            <div className="font-mono text-[11px] leading-relaxed">
              <b className="uppercase tracking-widest text-[10px] block mb-0.5">Pending Verification</b>
              Account ending {state?.last4 || "••••"} — two small deposits are on their way (1-2 business days).
              Enter both amounts below to finish. {scope === "funding"
                ? "Your business debit card still works for instant payouts in the meantime."
                : "Payroll can't include this employee until the account is verified and the rail authorized."}
            </div>
          </div>
          <form onSubmit={verify} className="flex flex-wrap items-end gap-3">
            {[["a", "Deposit 1 ($)"], ["b", "Deposit 2 ($)"]].map(([k, label]) => (
              <label key={k} className="block">
                <div className="overline mb-2">{label}</div>
                <input
                  data-testid={`manual-deposit-${k}-${scope}`}
                  required type="number" step="0.01" min="0.01" placeholder="0.32"
                  value={amounts[k]} onChange={(e) => setAmounts({ ...amounts, [k]: e.target.value })}
                  className="w-32 px-3 py-2 border-2 border-ink font-mono text-sm"
                />
              </label>
            ))}
            <button data-testid={`manual-verify-btn-${scope}`} disabled={busy} className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
              {busy ? "Verifying…" : "Confirm Deposits"}
            </button>
          </form>
        </>
      ) : (
        <>
          <div className="font-mono text-[11px] text-muted-foreground mb-4 leading-relaxed">
            No Plaid? Type the business account in and Stripe will send two small deposits to prove ownership.
            Slower to set up, identical once verified.
          </div>
          <form onSubmit={start} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="overline mb-2">Account holder name</div>
              <input data-testid={`manual-holder-${scope}`} required minLength={2} value={form.account_holder_name}
                onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <label className="block">
              <div className="overline mb-2">Account type</div>
              <select data-testid={`manual-type-${scope}`} value={form.account_type}
                onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm bg-white">
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </label>
            <label className="block">
              <div className="overline mb-2">Routing number (9 digits)</div>
              <input data-testid={`manual-routing-${scope}`} required inputMode="numeric" pattern="\d{9}" value={form.routing_number}
                onChange={(e) => setForm({ ...form, routing_number: e.target.value.replace(/\D/g, "") })}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <label className="block">
              <div className="overline mb-2">Account number</div>
              <input data-testid={`manual-account-${scope}`} required inputMode="numeric" pattern="\d{4,17}" value={form.account_number}
                onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="font-mono text-[10px] text-muted-foreground">
                Encrypted at rest · verified by Stripe · never sold
              </div>
              <button data-testid={`manual-start-btn-${scope}`} disabled={busy} className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
                {busy ? "Submitting…" : "Send Micro-Deposits"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
